import { z } from "zod";
export const SCHEMA_VERSION = 1;
export const PROTOCOL_VERSION = 1;
export const IdSchema = z.string().min(8).max(128);
export const IsoDateSchema = z.iso.date();
export const IsoInstantSchema = z.iso.datetime({ offset: true });
export const TimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const DeviceIdSchema = z.string().min(8).max(64);
export const RoleSchema = z.enum(["member", "coach", "owner"]);
/**
 * Ogni entita' che due dispositivi possono toccare separatamente porta con se'
 * quando e da chi e' stata cambiata: e' il minimo indispensabile perche' un
 * merge scelga senza chiedere. I due campi sono facoltativi soltanto per i
 * documenti nati prima di questa regola; `normalizeGym` li completa.
 */
export const SlotSchema = z.object({
    id: IdSchema,
    weekday: z.number().int().min(1).max(7),
    startTime: TimeSchema,
    durationMinutes: z.number().int().min(15).max(360),
    capacity: z.number().int().min(1).max(500),
    active: z.boolean().default(true),
    updatedAt: IsoInstantSchema.optional(),
    updatedBy: IdSchema.optional(),
});
/**
 * Uno slot cancellato non puo' semplicemente sparire dall'elenco: l'altro
 * dispositivo che non ha visto la cancellazione lo rimanderebbe indietro a ogni
 * scambio. La lapide resta, e resta per sempre: e' l'unica cosa che distingue
 * "rimosso" da "non l'ho ancora ricevuto".
 */
export const RetiredSlotSchema = z.object({
    id: IdSchema,
    updatedAt: IsoInstantSchema,
    updatedBy: IdSchema,
});
export const CalendarExceptionSchema = z.object({
    date: IsoDateSchema,
    closed: z.boolean().default(false),
    removedSlotIds: z.array(IdSchema).default([]),
    replacementSlots: z.array(SlotSchema).default([]),
    authorId: IdSchema,
    updatedAt: IsoInstantSchema,
});
export const GymSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    name: z.string().trim().min(2).max(80),
    timeZone: z.string().min(1),
    /**
     * La durata e' della palestra, non del singolo slot: gli slot durano tutti
     * uguale, e la decide il proprietario. Il campo per-slot `durationMinutes`
     * resta nello schema solo per i documenti nati prima di questa regola.
     */
    slotDurationMinutes: z.number().int().min(15).max(360).default(120),
    slots: z.array(SlotSchema),
    retiredSlots: z.array(RetiredSlotSchema).default([]),
    exceptions: z.array(CalendarExceptionSchema).default([]),
    updatedAt: IsoInstantSchema,
    updatedBy: IdSchema,
});
/**
 * `cleared` non e' una decisione dell'atleta: e' la lapide della decisione
 * precedente. Togliere l'eccezione dall'elenco non basterebbe, perche' l'altro
 * dispositivo la rimanderebbe indietro credendola nuova.
 */
export const ScheduleExceptionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("added"),
        date: IsoDateSchema,
        slotId: IdSchema,
        updatedAt: IsoInstantSchema.optional(),
        updatedBy: IdSchema.optional(),
    }),
    z.object({
        kind: z.literal("cancelled"),
        date: IsoDateSchema,
        sourceSlotId: IdSchema,
        updatedAt: IsoInstantSchema.optional(),
        updatedBy: IdSchema.optional(),
    }),
    z.object({
        kind: z.literal("moved"),
        date: IsoDateSchema,
        sourceSlotId: IdSchema,
        targetSlotId: IdSchema,
        updatedAt: IsoInstantSchema.optional(),
        updatedBy: IdSchema.optional(),
    }),
    z.object({
        kind: z.literal("cleared"),
        date: IsoDateSchema,
        sourceSlotId: IdSchema,
        updatedAt: IsoInstantSchema.optional(),
        updatedBy: IdSchema.optional(),
    }),
]);
/**
 * La verita' sulle ricorrenze e' questa lista, non `recurringSlotIds`: un
 * elenco di identificativi non sa dire se uno slot manca perche' e' stato
 * tolto o perche' non e' ancora arrivato. `recurringSlotIds` resta la
 * proiezione leggibile, derivata da qui a ogni normalizzazione.
 */
export const RecurringSlotSchema = z.object({
    slotId: IdSchema,
    active: z.boolean(),
    updatedAt: IsoInstantSchema,
    updatedBy: IdSchema,
});
export const ScheduleConflictSchema = z.object({
    occurrenceKey: z.string().min(1),
    candidates: z.array(ScheduleExceptionSchema).min(2),
    resolvedTo: ScheduleExceptionSchema.optional(),
});
export const MemberScheduleSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    gymId: IdSchema,
    memberId: IdSchema,
    displayName: z.string().trim().min(1).max(80),
    recurringSlotIds: z.array(IdSchema),
    recurring: z.array(RecurringSlotSchema).default([]),
    exceptions: z.array(ScheduleExceptionSchema),
    conflicts: z.array(ScheduleConflictSchema).default([]),
    updatedAt: IsoInstantSchema,
});
/**
 * Quello che l'atleta puo' sapere degli altri: quanti sono, non chi sono. Il
 * digest lo compone chi ha in mano tutte le pianificazioni — lo staff — e viaggia
 * come qualsiasi altra operazione firmata, cifrata per i pari della palestra.
 */
export const OccupancyEntrySchema = z.object({
    occurrenceKey: z.string().min(1),
    date: IsoDateSchema,
    slotId: IdSchema,
    count: z.number().int().min(0).max(100_000),
    capacity: z.number().int().min(1).max(500),
    updatedAt: IsoInstantSchema,
    updatedBy: IdSchema,
});
export const OccupancyDigestSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    gymId: IdSchema,
    entries: z.array(OccupancyEntrySchema),
    generatedAt: IsoInstantSchema,
    generatedBy: IdSchema,
});
export const SyncRequestSchema = z.object({
    requesterId: IdSchema,
    requestedAt: IsoInstantSchema,
    /**
     * Chi chiede allineamento puo' essere l'altro dispositivo della stessa
     * persona. Senza distinguere il dispositivo, chi riceve la richiesta non
     * saprebbe se e' la propria e finirebbe per non rispondere mai.
     */
    deviceId: DeviceIdSchema.optional(),
    /** Inventario cifrato: l'assenza mantiene il recupero completo dei vecchi client. */
    knownDocuments: z
        .record(z.string().max(200), z.string().max(80))
        .refine((value) => Object.keys(value).length <= 1000)
        .optional(),
    needsChannelPass: z.boolean().optional(),
});
/** Il contenuto del broadcast: nessun roster, per non rivelare nomi agli atleti. */
export const BroadcastMessageSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    body: z.string().trim().min(1).max(1_000),
    createdBy: IdSchema,
    createdAt: IsoInstantSchema,
});
/** Lo snapshot dei destinatari resta cifrato esclusivamente per lo staff. */
export const BroadcastManifestSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    broadcastId: IdSchema,
    gymId: IdSchema,
    contentEnvelopeId: IdSchema,
    senderId: IdSchema,
    recipients: z
        .array(z.object({
        memberId: IdSchema,
        displayNameAtSend: z.string().trim().min(1).max(80).optional(),
    }))
        .max(500),
    createdAt: IsoInstantSchema,
});
/** Ricevuta applicativa: `read` implica semanticamente `received`. */
export const BroadcastReceiptSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    broadcastId: IdSchema,
    memberId: IdSchema,
    state: z.enum(["received", "read"]),
    occurredAt: IsoInstantSchema,
});
export const RoleCertificateSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    subjectId: IdSchema,
    subjectSigningKey: z.string().min(40),
    subjectEncryptionKey: z.string().min(40),
    role: RoleSchema,
    issuedBy: IdSchema,
    issuedAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema.optional(),
    revokedAt: IsoInstantSchema.optional(),
    signature: z.string().min(40),
});
export const RoleRevocationSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    subjectId: IdSchema,
    revokedBy: IdSchema,
    revokedAt: IsoInstantSchema,
    signature: z.string().min(40),
});
/** Capability leggibile dal relay che distingue lo staff dai normali membri. */
export const PushWakeGrantSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    channelId: IdSchema,
    subjectId: IdSchema,
    permission: z.literal("broadcast"),
    issuerSigningKey: z.string().min(40),
    issuedAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema,
    signature: z.string().min(40),
});
export const PushWakeRevocationSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    channelId: IdSchema,
    subjectId: IdSchema,
    revokedAt: IsoInstantSchema,
    issuerSigningKey: z.string().min(40),
    signature: z.string().min(40),
});
export const PushWakeSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    channelId: IdSchema,
    envelopeId: IdSchema,
    senderId: IdSchema,
    recipientIds: z.array(IdSchema).min(1).max(500),
    createdAt: IsoInstantSchema,
    grant: PushWakeGrantSchema.optional(),
    signature: z.string().min(40),
});
export const WebPushSubscriptionSchema = z.object({
    endpoint: z.url().max(4_096),
    expirationTime: z.number().nonnegative().nullable().optional(),
    keys: z.object({
        p256dh: z.string().min(16).max(512),
        auth: z.string().min(8).max(256),
    }),
});
/**
 * Un invito speso, o ritirato prima che qualcuno lo spendesse. E' la forma
 * firmata del `nonce` che nessuno consumava: chi accoglie un nuovo arrivato
 * registra qui l'invito con cui e' entrato e lo dice ai pari, cosi' un secondo
 * uso dello stesso link non autorizza nessuno.
 *
 * `consumedBy` assente significa «ritirato»: l'invito non ha aperto nulla e non
 * aprira' piu' niente.
 */
export const InviteConsumptionSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    inviteId: IdSchema,
    consumedBy: IdSchema.optional(),
    recordedBy: IdSchema,
    recordedAt: IsoInstantSchema,
    signature: z.string().min(40),
});
/**
 * Il rifiuto detto in faccia. Un invito gia' speso da un altro veniva scartato
 * in silenzio da chi lo riceveva: chi lo aveva presentato restava «in attesa
 * del coach» per sempre, con un profilo che sembrava buono, prenotazioni che
 * non arrivavano a nessuno e nessun modo di capirlo. Questa e' la risposta che
 * mancava, firmata da chi scarta, indirizzata a chi ha presentato l'invito.
 *
 * `reason`: `spent` se l'invito ha gia' fatto entrare qualcun altro,
 * `withdrawn` se e' stato ritirato prima di essere usato.
 */
export const InviteRejectionSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    inviteId: IdSchema,
    /** A chi e' rivolto: senza, sarebbe un rifiuto che chiunque puo' rigiocare. */
    subjectId: IdSchema,
    reason: z.enum(["spent", "withdrawn"]),
    rejectedBy: IdSchema,
    rejectedAt: IsoInstantSchema,
    signature: z.string().min(40),
});
export const SignedOperationSchema = z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    authorId: IdSchema,
    authorRole: RoleSchema,
    createdAt: IsoInstantSchema,
    parents: z.array(IdSchema),
    kind: z.enum([
        "gym.updated",
        "schedule.updated",
        "role.issued",
        "role.revoked",
        "invite.consumed",
        "invite.rejected",
        "conflict.resolved",
        "sync.requested",
        "occupancy.published",
        "broadcast.published",
        "broadcast.manifest",
        "broadcast.receipt",
        "push.wake.granted",
    ]),
    payload: z.unknown(),
    signature: z.string().min(40),
});
/**
 * Il lasciapassare per un canale del relay. Non nomina la palestra — ne' il
 * `gymId` ne' il nome — perche' il relay lo deve poter verificare senza imparare
 * nulla di cio' che trasporta: gli bastano il canale, chi ammette e chi e'
 * ammesso.
 *
 * `subjectId` assente significa "al portatore": e' la forma che viaggia dentro
 * un invito, dove il destinatario non ha ancora un'identita' nota a chi invita.
 * Un pass al portatore deve scadere; quello nominativo puo' non scadere, perche'
 * vale per una persona sola e la si toglie togliendole il ruolo.
 */
export const ChannelPassSchema = z.object({
    channelId: IdSchema,
    subjectId: IdSchema.optional(),
    /** Solo lo staff estende la catena: un atleta non ammette nessun altro. */
    canIssue: z.boolean(),
    issuerId: IdSchema,
    issuerSigningKey: z.string().min(40),
    issuedAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema.optional(),
    signature: z.string().min(40),
});
/**
 * La catena che va dall'ancora del canale — la chiave del fondatore, da cui il
 * canale stesso e' derivato — fino a chi si presenta.
 *
 * Da quando invitare e' del solo fondatore, l'unica catena che questo client
 * produce e' lunga un anello: fondatore ▸ invitato. Il tetto resta a quattro
 * perche' e' un limite di *forma*, quello che impedisce a una catena inventata
 * di costare tempo, ed e' condiviso con il porting Go del relay; a dire chi puo'
 * ammettere chi sono `canIssue` e `verifyInviteChannelProof`, non questo numero.
 */
export const ChannelProofSchema = z.array(ChannelPassSchema).min(1).max(4);
export const InvitePayloadSchema = z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    id: IdSchema,
    gymId: IdSchema,
    gymName: z.string().min(2).max(80),
    /**
     * Il canale non si deriva piu' dal `gymId`: chi entra deve sapere a quale
     * casella del relay presentarsi, e la firma dell'invito e' cio' che gli
     * impedisce di essere dirottato altrove.
     */
    channelId: IdSchema,
    role: RoleSchema,
    issuedBy: IdSchema,
    issuedAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema,
    nonce: z.string().min(16),
});
/**
 * Il nome della cassetta del link corto sul relay: 9 byte casuali, 72 bit,
 * base64url senza padding. Non e' un segreto di cifratura — quello e' la chiave
 * nel frammento, che il relay non vede mai — ma non e' enumerabile.
 */
export const InviteLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{12}$/u);
/** Il blob cifrato del link. Il tetto vero, in byte decodificati, e' del relay. */
export const InviteLinkBlobSchema = z
    .string()
    .min(32)
    .max(8 * 1024);
/**
 * Un invito emesso, per poterlo mostrare e ritirare. `token` e `url` mancano
 * solo negli inviti nati prima del link corto; l'URL contiene la chiave di
 * decifratura, e viaggia unicamente dentro il backup cifrato del profilo —
 * e' cio' che rende «Mostra» e «Ritira» comandi di qualsiasi dispositivo.
 */
export const IssuedInviteSchema = z.object({
    id: IdSchema,
    role: RoleSchema,
    issuedAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema,
    token: InviteLinkTokenSchema.optional(),
    url: z.string().min(16).max(512).optional(),
});
export const SignedInviteSchema = z.object({
    payload: InvitePayloadSchema,
    issuerSigningKey: z.string().min(40),
    issuerEncryptionKey: z.string().min(40),
    /** Il lasciapassare al portatore con cui l'invitato entra nel canale. */
    channelProof: ChannelProofSchema,
    signature: z.string().min(40),
});
export const SyncEnvelopeSchema = z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    id: IdSchema,
    channelId: IdSchema,
    senderId: IdSchema,
    createdAt: IsoInstantSchema,
    expiresAt: IsoInstantSchema,
    nonce: z.string().min(16),
    ciphertext: z.string().min(16),
    recipients: z.array(z.object({ recipientId: IdSchema, ephemeralPublicKey: z.string(), wrappedKey: z.string() })),
});
/**
 * Il profilo locale era finora una forma implicita del solo client web. Perche'
 * possa attraversare il relay e tornare su un dispositivo vergine deve essere
 * validabile: quello che rientra viene riconosciuto, non dato per buono.
 */
export const AuthorizedPeerSchema = z.object({
    id: IdSchema,
    signingPublicKey: z.string().min(40),
    encryptionPublicKey: z.string().min(40),
    role: RoleSchema,
});
export const LocalProfileSchema = z.object({
    displayName: z.string().trim().min(1).max(80),
    role: RoleSchema,
    gymId: IdSchema,
    /**
     * La casella del relay e il lasciapassare per entrarci: senza, il profilo
     * resta usabile in locale ma non ha modo di farsi riconoscere dal canale.
     * Facoltativi perche' i profili nati prima della prova di appartenenza
     * esistono ancora sui dispositivi.
     */
    channelId: IdSchema.optional(),
    channelProof: ChannelProofSchema.optional(),
    pushWakeGrant: PushWakeGrantSchema.optional(),
    peers: z.array(AuthorizedPeerSchema),
    /**
     * Le revoche, non solo le loro conseguenze. Togliere un pari dall'elenco non
     * bastava: alla prima operazione successiva risultava «autore sconosciuto», e
     * per un autore sconosciuto l'invito con cui era entrato — ancora firmato,
     * ancora valido — tornava a valere. La revoca dev'essere una cosa che si
     * *ricorda*, non un'assenza; e va nel backup, altrimenti un ripristino la
     * disfa. Ha un valore predefinito perche' i profili nati prima non ce l'hanno.
     */
    revokedPeers: z.array(RoleRevocationSchema).optional(),
    /**
     * Gli inviti gia' spesi o ritirati. Un invito era un lasciapassare al
     * portatore senza limite di usi: il `nonce` esisteva dentro `InvitePayload`
     * ma nessuno lo consumava.
     */
    spentInvites: z.array(InviteConsumptionSchema).optional(),
    /**
     * Presente solo su un dispositivo entrato con un invito che non valeva piu':
     * e' il riconoscimento «quello scartato sono io». Senza, quel dispositivo
     * restava «in attesa del coach» a tempo indeterminato, con un profilo che
     * sembrava buono e prenotazioni che non arrivavano da nessuna parte.
     */
    rejectedAccess: z
        .object({
        inviteId: IdSchema,
        reason: z.enum(["spent", "withdrawn"]),
        rejectedAt: IsoInstantSchema,
        rejectedBy: IdSchema,
    })
        .optional(),
    /**
     * Presente solo sul dispositivo di chi e' stato revocato: e' il
     * riconoscimento «il revocato sono io». L'envelope `role.revoked` arrivava
     * gia' fin qui, ma il client lo applicava senza mai accorgersi che il
     * soggetto era se stesso — e l'app continuava a offrire prenotazioni che i
     * pari scartavano in silenzio. Va nel backup: un ripristino non deve disfare
     * la notizia. Sparisce accettando un invito nuovo.
     */
    revokedAccess: z.object({ revokedAt: IsoInstantSchema, revokedBy: IdSchema }).optional(),
});
/** Il tetto vero, in byte decodificati, e' del relay: qui si limita l'assurdo. */
export const ProfileBackupBlobSchema = z
    .string()
    .min(32)
    .max(512 * 1024);
export const ProfileBackupSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    profile: LocalProfileSchema,
    acceptedInvite: SignedInviteSchema.optional(),
    gym: GymSchema.optional(),
    ownSchedule: MemberScheduleSchema.optional(),
    /**
     * Solo per lo staff: un coach che apre l'app su un secondo dispositivo deve
     * vedere le presenze anche quando nessun altro dispositivo e' collegato.
     * Viene omesso se non ci sta nei limiti del blob.
     */
    schedules: z.array(MemberScheduleSchema).optional(),
    /** Costa pochi KB e rende leggibile la capienza anche se nessun coach e' collegato. */
    occupancy: OccupancyDigestSchema.optional(),
    /**
     * Gli inviti emessi e non ancora spesi. Vivevano in un solo dispositivo
     * (`getMeta`), quindi un invito creato dal telefono non era ritirabile dal
     * computer e su un telefono nuovo l'elenco ripartiva vuoto mentre gli inviti
     * restavano vivi la' fuori. Costano una manciata di byte a voce, dentro un
     * tetto di 64 KB.
     */
    issuedInvites: z.array(IssuedInviteSchema).optional(),
    updatedAt: IsoInstantSchema,
});
export const EnvelopeReplacementSchema = z.object({
    key: z.string().min(16).max(128),
    version: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
/** Decisione dell'owner, leggibile dal relay e separata dalle revoche push. */
export const RecipientStatusSchema = z.object({
    schemaVersion: z.literal(1),
    purpose: z.literal("recipient.status"),
    channelId: IdSchema,
    subjectId: IdSchema,
    active: z.boolean(),
    changedAt: IsoInstantSchema,
    issuerSigningKey: z.string().min(40),
    signature: z.string().min(40),
});
/** Elenco completo dichiarato dal proprietario: il relay non conosce i nomi. */
export const RecipientRosterSchema = z.object({
    schemaVersion: z.literal(1),
    purpose: z.literal("recipient.roster"),
    channelId: IdSchema,
    recipientIds: z.array(IdSchema).min(1).max(500),
    changedAt: IsoInstantSchema,
    issuerSigningKey: z.string().min(40),
    signature: z.string().min(40),
});
export const RelayClientMessageSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("hello"),
        channelId: IdSchema,
        clientId: IdSchema,
        signingPublicKey: z.string().min(40),
        /**
         * La stessa persona su telefono e computer e' la stessa identita': senza
         * distinguere il dispositivo, la conferma di lettura di uno dei due farebbe
         * scomparire l'arretrato dell'altro. Facoltativo per i client piu' vecchi.
         */
        deviceId: DeviceIdSchema.optional(),
        since: IsoInstantSchema.optional(),
    }),
    z.object({
        type: z.literal("authenticate"),
        channelId: IdSchema,
        clientId: IdSchema,
        challenge: z.string().min(16),
        /**
         * Assente solo per la casella personale, che il relay ricalcola da solo
         * dall'identita' di chi si presenta. Per il canale di una palestra e'
         * l'unica cosa che distingue un membro da chiunque ne conosca il nome.
         */
        channelProof: ChannelProofSchema.optional(),
        signature: z.string().min(40),
    }),
    z.object({
        type: z.literal("publish"),
        envelope: SyncEnvelopeSchema,
        wake: PushWakeSchema.optional(),
        deliveryMode: z.literal("transient").optional(),
        replacement: EnvelopeReplacementSchema.optional(),
    }),
    z.object({ type: z.literal("ack"), envelopeId: IdSchema }),
    z.object({ type: z.literal("recipient.status"), status: RecipientStatusSchema }),
    z.object({ type: z.literal("recipient.roster"), roster: RecipientRosterSchema }),
    z.object({ type: z.literal("push.subscribe"), subscription: WebPushSubscriptionSchema }),
    z.object({ type: z.literal("push.unsubscribe") }),
    z.object({ type: z.literal("push.grant.revoke"), revocation: PushWakeRevocationSchema }),
    z.object({ type: z.literal("ping"), sentAt: IsoInstantSchema }),
    z.object({ type: z.literal("backup.put"), blob: ProfileBackupBlobSchema }),
    z.object({ type: z.literal("backup.get") }),
    /**
     * La cancellazione della copia custodita dal relay. Non porta parametri:
     * la chiave e' l'identita' che ha superato la challenge, quindi nessuno puo'
     * cancellare altro che se stesso. La risposta e' un `backup` vuoto, che e'
     * anche la conferma che non c'e' piu' nulla.
     */
    z.object({ type: z.literal("backup.delete") }),
    /**
     * Deposita il blob cifrato di un link di invito. Passa dalla connessione
     * autenticata, deliberatamente: non serve un endpoint pubblico di scrittura,
     * il relay applica una quota per identita' come gia' fa per gli envelope, e
     * non c'e' una firma nuova da progettare. Il relay non puo' leggere il blob:
     * la chiave sta nel frammento dell'URL e non lascia mai il browser.
     */
    z.object({
        type: z.literal("invite.put"),
        token: InviteLinkTokenSchema,
        blob: InviteLinkBlobSchema,
        expiresAt: IsoInstantSchema,
    }),
    /**
     * Il ritiro del link: da qui in poi `GET /l/<token>` risponde `410 Gone`.
     * Cancella solo cio' che la stessa identita' ha depositato. Non revoca
     * l'invito per chi ha gia' scaricato il blob: quella resta a `spentInvites`.
     */
    z.object({ type: z.literal("invite.delete"), token: InviteLinkTokenSchema }),
]);
/**
 * Chi ha risposto dall'altra parte del socket. Il server corrente dichiara
 * `go`; `js` resta nello schema per leggere metadati prodotti dal precedente
 * relay senza interrompere una sessione già installata.
 */
export const RelayServerIdentitySchema = z.object({
    kind: z.enum(["js", "go"]),
    /** Tag di build dichiarato dal relay. */
    version: z.string().min(1).max(64),
});
export const RelayServerMessageSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("challenge"), challenge: z.string().min(16) }),
    /**
     * `server` e' facoltativo di proposito: un relay piu' vecchio del campo resta
     * legittimo, e il dispositivo lo dichiara «non dichiarato» invece di
     * rifiutarne l'handshake.
     */
    z.object({
        type: z.literal("ready"),
        serverTime: IsoInstantSchema,
        server: RelayServerIdentitySchema.optional(),
        pushPublicKey: z.string().min(40).optional(),
    }),
    z.object({ type: z.literal("envelope"), envelope: SyncEnvelopeSchema }),
    z.object({ type: z.literal("published"), envelopeId: IdSchema }),
    z.object({ type: z.literal("recipient.status.stored"), status: RecipientStatusSchema }),
    z.object({ type: z.literal("recipient.roster.stored"), roster: RecipientRosterSchema }),
    z.object({ type: z.enum(["push.subscribed", "push.unsubscribed", "push.grant.revoked"]) }),
    z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
    z.object({
        type: z.literal("pong"),
        sentAt: IsoInstantSchema,
        serverTime: IsoInstantSchema,
    }),
    // `null` non e' un errore: "questa identita' non ha un backup" e' una risposta
    // legittima, ed e' quella che apre il percorso di ripiego con l'invito.
    z.object({ type: z.literal("backup"), blob: ProfileBackupBlobSchema.nullable() }),
    /**
     * La conferma che il blob del link e' custodito. Chi crea l'invito la attende
     * prima di mostrare l'URL: un link consegnato senza la cassetta dietro
     * aprirebbe un 410 al primo tocco.
     */
    z.object({ type: z.literal("invite.stored"), token: InviteLinkTokenSchema }),
]);
